import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Navigation } from "../components/Navigation";
import { ViewsBadge } from "../components/prodotti/ViewsBadge";
import { isSoldOut, SoldOutStamp, SOLD_OUT_IMG } from "../components/prodotti/SoldOutStamp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// SEO Category Page Component
export const CategorySEOPage = ({ category }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const categoryMeta = {
    "Truck Tuning Collection": {
      title: "Truck Tuning Collection Centauria - Modellini Camion Tuning",
      h1: "Truck Tuning Collection",
      description: "Scopri la collezione completa Truck Tuning di Centauria. Modellini di camion tuning in scala, dettagliati e curati. Spedizioni internazionali assicurate.",
      longText: "La Truck Tuning Collection di Centauria rappresenta una delle collezioni piu apprezzate nel mondo del modellismo. Ogni modello riproduce fedelmente i camion tuning piu iconici, con dettagli straordinari e finiture di alta qualita. Dalla carrozzeria personalizzata agli interni curati, ogni pezzo e un'opera d'arte in miniatura. Disponibile con spedizione assicurata in tutto il mondo."
    },
    "Racing Car Transporters": {
      title: "Racing Car Transporters Centauria - Bisarche F1 in Scala",
      h1: "Racing Car Transporters",
      description: "Collezione Racing Car Transporters Centauria. Bisarche e trasportatori delle scuderie F1 in scala. Ferrari, McLaren, Williams e tutte le scuderie storiche.",
      longText: "La collezione Racing Car Transporters di Centauria e dedicata ai trasportatori delle grandi scuderie di Formula 1. Ogni modello riproduce le bisarche iconiche che hanno accompagnato le monoposto sui circuiti di tutto il mondo. Da Ferrari a McLaren, da Williams a Lotus, una collezione imperdibile per gli appassionati di F1 e modellismo."
    },
    "Pokemon": {
      title: "Carte Pokemon TCG - Set, ETB, Bustine e Collezioni Speciali",
      h1: "Pokemon TCG",
      description: "Carte Pokemon TCG in italiano e inglese. Elite Trainer Box, bustine, set speciali e collezioni esclusive. Preordini disponibili con spedizione internazionale.",
      longText: "Trova le migliori carte Pokemon Trading Card Game. Offriamo Elite Trainer Box (ETB), bustine, set base, collezioni speciali e prodotti esclusivi. Disponibili in italiano e inglese con possibilita di preordine sui nuovi set in uscita. Spedizioni assicurate in tutto il mondo per proteggere i tuoi acquisti."
    },
    "Auto da corsa": {
      title: "Modellini Auto da Corsa F1 - Ferrari LookSmart 1:43",
      h1: "Auto da Corsa e Modellismo F1",
      description: "Modellini F1 Ferrari LookSmart in scala 1:43. Leclerc, Hamilton, Sainz e tutte le monoposto storiche. Caschi piloti e accessori F1. Spedizioni internazionali.",
      longText: "La nostra selezione di modellini Formula 1 include i migliori produttori come LookSmart, BBR e Spark. Troverai le monoposto Ferrari, McLaren, Red Bull e Mercedes in scala 1:43 e 1:18. Dai modelli attuali di Leclerc e Hamilton alle monoposto storiche di Schumacher e Senna. Disponibili anche caschi piloti in scala e accessori da collezione."
    }
  };

  const meta = categoryMeta[category] || { title: category, h1: category, description: "", longText: "" };

  // Canonical slug — must match the actual route in App.js
  const categorySlugMap = {
    "Truck Tuning Collection": "truck-tuning",
    "Racing Car Transporters": "racing-car-transporters",
    "Pokemon": "pokemon-tcg",
    "Auto da corsa": "auto-da-corsa-f1",
  };
  const canonicalSlug = categorySlugMap[category] || category.toLowerCase().replace(/\s/g, '-');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/public/articles`);
        const lowerCat = category.toLowerCase();
        const filtered = response.data.filter(a => {
          const aCat = (a.category || '').toLowerCase();
          const aTitle = (a.title || '').toLowerCase();
          // Match by exact category OR by keywords
          if (aCat === lowerCat) return true;
          if (aTitle.includes(lowerCat)) return true;
          // Special matching for F1/Auto da corsa: match anything Ferrari, F1, etc.
          if (lowerCat.includes('auto da corsa')) {
            return aTitle.match(/ferrari|f1|formula|leclerc|hamilton|looksmart|red bull|mclaren|mercedes/i) ||
                   aCat.match(/ferrari|f1|formula|corsa/i);
          }
          if (lowerCat.includes('pokemon')) {
            return aTitle.match(/pokemon|pikachu|charizard|tcg/i) || aCat.match(/pokemon/i);
          }
          if (lowerCat.includes('racing car')) {
            return aTitle.match(/transporter|bisarca|racing car/i) || aCat.match(/racing/i);
          }
          if (lowerCat.includes('truck tuning')) {
            return aTitle.match(/truck.*tuning|tuning.*truck/i) || aCat.includes('truck');
          }
          return false;
        });
        setArticles(filtered);
      } catch (e) { /* ignore */ }
      setLoading(false);
    };
    fetchArticles();
  }, [category]);

  const formatPriceWithIva = (price, iva) => {
    return (price * (1 + (iva || 0) / 100)).toFixed(2) + " €";
  };

  // Memoize JSON-LD schema to avoid recomputing JSON.stringify on every render
  const collectionSchemaJson = useMemo(() => JSON.stringify({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": meta.title,
    "description": meta.description,
    "url": `https://edicolavillaricca.com/collezione/${canonicalSlug}`,
    "isPartOf": { "@type": "WebSite", "name": "Edicola Cerasuolo", "url": "https://edicolavillaricca.com" },
    "breadcrumb": { "@type": "BreadcrumbList", "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://edicolavillaricca.com" },
      { "@type": "ListItem", "position": 2, "name": "Prodotti", "item": "https://edicolavillaricca.com/prodotti" },
      { "@type": "ListItem", "position": 3, "name": meta.h1 }
    ]},
    "mainEntity": articles.length > 0 ? {
      "@type": "ItemList",
      "numberOfItems": articles.length,
      "itemListElement": articles.slice(0, 24).map((a, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "item": {
          "@type": "Product",
          "name": a.title,
          "url": `https://edicolavillaricca.com/prodotto/${a.slug || a.id}`,
          "image": a.images_url?.[0] || (a.photo_url ? `https://edicolavillaricca.com${a.photo_url}` : undefined),
          "sku": a.sku || a.id,
          "offers": {
            "@type": "Offer",
            "price": (a.price * (1 + (a.iva_percentage || 0) / 100)).toFixed(2),
            "priceCurrency": "EUR",
            "availability": (a.quantity_stock > 0) ? "https://schema.org/InStock" :
                             (a.availability_type === "preorder") ? "https://schema.org/PreOrder" :
                             "https://schema.org/OutOfStock",
            "url": `https://edicolavillaricca.com/prodotto/${a.slug || a.id}`
          }
        }
      }))
    } : undefined
  }), [meta, canonicalSlug, articles]);

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* React 19 native head tags */}
      <title>{`${meta.title} | Edicola Cerasuolo`}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={`https://edicolavillaricca.com/collezione/${canonicalSlug}`} />
      <meta property="og:title" content={`${meta.title} | Edicola Cerasuolo`} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:type" content="website" />

      <Navigation />
      {/* Breadcrumb */}
      <nav className="bg-[#0d0d14] py-3 px-4 text-sm" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto">
          <ol className="flex items-center gap-2 text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <a href="/" className="hover:text-amber-400" itemProp="item"><span itemProp="name">Home</span></a>
              <meta itemProp="position" content="1" />
            </li>
            <li className="text-gray-600">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <a href="/prodotti" className="hover:text-amber-400" itemProp="item"><span itemProp="name">Prodotti</span></a>
              <meta itemProp="position" content="2" />
            </li>
            <li className="text-gray-600">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-amber-400" itemProp="name">{meta.h1}</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <header className="py-12 bg-[#0a0a0f]">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{meta.h1}</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">{meta.description}</p>
        </div>
      </header>

      {/* Products Grid */}
      <section className="py-12 bg-[#0d0d14]">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Caricamento...</div>
          ) : articles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">Nessun prodotto disponibile in questa categoria al momento.</p>
              <a href="/prodotti" className="text-amber-400 hover:text-amber-300">Vedi tutti i prodotti</a>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {articles.map(article => (
                <a key={article.id} href={`/prodotti?filter=${encodeURIComponent(category)}`} className="group relative bg-[#12121a] border border-gray-800 rounded-xl overflow-hidden hover:border-amber-500/30 transition-all duration-300">
                  <ViewsBadge articleId={article.id} className="top-2 right-2" />
                  {(article.images_url?.[0] || article.photo_url) && (
                    <div className="aspect-square overflow-hidden bg-white relative">
                      <img src={article.images_url?.[0] || `${BACKEND_URL}${article.photo_url}`} alt={article.title} className={`w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300 ${isSoldOut(article) ? SOLD_OUT_IMG : ""}`} onError={(e) => { e.target.style.display = 'none'; }} />
                      {isSoldOut(article) && <SoldOutStamp articleId={article.id} size="sm" />}
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="text-white text-sm font-semibold line-clamp-2 mb-2 group-hover:text-amber-400">{article.title}</h3>
                    <div className="flex justify-between items-center">
                      <span className="text-amber-400 font-bold">{formatPriceWithIva(article.price, article.iva_percentage)}</span>
                      {article.quantity_stock > 0 ? (
                        <span className="text-green-500 text-xs flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>Disponibile</span>
                      ) : article.availability_type === 'preorder' ? (
                        <span className="text-blue-400 text-xs">Preordine</span>
                      ) : null}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SEO Long Text */}
      <section className="py-12 bg-[#0a0a0f]">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-xl font-bold text-white mb-4">Informazioni su {meta.h1}</h2>
          <p className="text-gray-400 text-sm leading-relaxed">{meta.longText}</p>
          <div className="mt-6 flex gap-3">
            <a href="/prodotti" className="text-amber-400 hover:text-amber-300 text-sm font-medium">Vedi tutti i prodotti →</a>
            <a href="/" className="text-gray-500 hover:text-gray-400 text-sm">Torna alla homepage</a>
          </div>
        </div>
      </section>

      {/* Schema.org Product Collection + ItemList (rich snippet carousel) */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: collectionSchemaJson }} />
    </div>
  );
};

// Blog Page Component
const blogArticles = [
  {
    slug: "guida-modellini-ferrari-looksmart-1-43",
    title: "Guida ai Modellini Ferrari LookSmart 1:43 — I Migliori da Collezione",
    date: "2026-05-12",
    category: "Guide",
    excerpt: "Scopri quali sono i modellini Ferrari LookSmart 1:43 piu ricercati dai collezionisti. Da Leclerc a Schumacher, la guida completa.",
    content: `I modellini Ferrari in scala 1:43 di LookSmart sono tra i piu apprezzati dai collezionisti di tutto il mondo. La qualita dei dettagli, la fedelta delle livree e la cura degli interni rendono ogni pezzo un'opera d'arte in miniatura.

I modelli piu ricercati includono le monoposto di Charles Leclerc (SF-23, SF-24), le storiche di Michael Schumacher (F2004, F1-2000) e i modelli speciali come le versioni Miami e Monza.

LookSmart utilizza resina di alta qualita per garantire dettagli impossibili da ottenere con il die-cast tradizionale. Ogni modello viene dipinto a mano e controllato prima della consegna.

Per i collezionisti, consigliamo di preordinare i nuovi modelli appena disponibili, poiche le edizioni LookSmart sono spesso limitate e vanno esaurite rapidamente.

Presso Edicola Cerasuolo offriamo tutta la gamma LookSmart con spedizioni assicurate in tutto il mondo. Contattaci per disponibilita e preordini.`
  },
  {
    slug: "pokemon-tcg-2026-tutti-i-set-in-uscita",
    title: "Pokemon TCG 2026: Tutti i Set in Uscita e Date di Rilascio",
    date: "2026-05-10",
    category: "Notizie",
    excerpt: "Calendario completo dei set Pokemon TCG previsti per il 2026. ETB, collezioni speciali e bustine: tutte le date e i prezzi.",
    content: `Il 2026 si preannuncia un anno ricchissimo per il Pokemon Trading Card Game. Ecco tutti i set confermati e le date di uscita previste.

I nuovi set incluono espansioni con meccaniche innovative e carte da collezione esclusive. Gli Elite Trainer Box (ETB) rimangono il formato piu popolare, offrendo bustine, accessori e carte promo.

Per i collezionisti italiani, i set vengono rilasciati in italiano circa 2-3 settimane dopo l'uscita internazionale. Consigliamo di preordinare per assicurarsi le prime edizioni.

Presso Edicola Cerasuolo puoi preordinare tutti i nuovi set Pokemon TCG con garanzia di prezzo e spedizione assicurata. Offriamo sia versioni italiane che inglesi.`
  },
  {
    slug: "racing-car-transporters-centauria-collezione-completa",
    title: "Racing Car Transporters Centauria: Guida alla Collezione Completa",
    date: "2026-05-08",
    category: "Guide",
    excerpt: "Tutto quello che devi sapere sulla collezione Racing Car Transporters di Centauria. Numeri, scuderie, prezzi e dove trovarli.",
    content: `La collezione Racing Car Transporters di Centauria e una delle serie piu amate dai collezionisti di Formula 1. Ogni modello riproduce i trasportatori ufficiali delle scuderie che hanno fatto la storia del motorsport.

La collezione comprende i trasportatori di Ferrari, McLaren, Williams, Lotus, Brabham e molte altre scuderie storiche. Ogni modello e realizzato in scala con dettagli fedeli alle livree originali dell'epoca.

I numeri piu ricercati sono quelli delle scuderie Ferrari (in tutte le epoche), seguiti da McLaren e Williams. Alcuni numeri sono diventati rari e molto ricercati sul mercato secondario.

Edicola Cerasuolo e specializzata nella vendita di Racing Car Transporters con spedizioni in tutto il mondo. Contattaci per verificare la disponibilita dei numeri che cerchi.`
  },
  {
    slug: "truck-tuning-collection-centauria-guida",
    title: "Truck Tuning Collection Centauria: I Modelli Piu Belli e Rari",
    date: "2026-05-05",
    category: "Guide",
    excerpt: "La guida definitiva alla Truck Tuning Collection di Centauria. Scopri i modelli piu belli, rari e ricercati della collezione.",
    content: `La Truck Tuning Collection di Centauria rappresenta il meglio del modellismo dedicato ai camion personalizzati. Ogni modello riproduce fedelmente i truck tuning piu spettacolari visti nelle fiere europee.

La collezione si distingue per l'incredibile livello di dettaglio: dalle cromature agli aerografi personalizzati, dagli interni rifiniti alle luci LED in alcuni modelli speciali.

I modelli piu ricercati sono quelli con livree particolarmente elaborate e le edizioni limitate pubblicate nei numeri speciali. Alcuni pezzi sono diventati veri e propri oggetti da collezione con valutazioni in crescita.

Presso Edicola Cerasuolo offriamo la collezione completa Truck Tuning con possibilita di spedizione assicurata in tutto il mondo. I numeri arretrati sono disponibili su richiesta.`
  }
];

export const BlogPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* React 19 native head tags */}
      <title>Blog e Guide | Edicola Cerasuolo - Modellismo F1, Pokemon TCG</title>
      <meta name="description" content="Blog Edicola Cerasuolo: guide e approfondimenti sul modellismo F1 Ferrari LookSmart, set Pokemon TCG, Racing Car Transporters Centauria e Truck Tuning Collection." />
      <link rel="canonical" href="https://edicolavillaricca.com/blog" />
      <meta property="og:title" content="Blog e Guide | Edicola Cerasuolo" />
      <meta property="og:type" content="website" />

      <Navigation />
      <nav className="bg-[#0d0d14] py-3 px-4 text-sm">
        <div className="max-w-7xl mx-auto">
          <ol className="flex items-center gap-2 text-gray-500">
            <li><a href="/" className="hover:text-amber-400">Home</a></li>
            <li className="text-gray-600">/</li>
            <li className="text-amber-400">Blog</li>
          </ol>
        </div>
      </nav>
      <header className="py-12 bg-[#0a0a0f]">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Blog e Guide</h1>
          <p className="text-gray-400">Notizie, guide e approfondimenti sul mondo del modellismo e delle collezioni</p>
        </div>
      </header>
      <section className="py-12 bg-[#0d0d14]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="space-y-6">
            {blogArticles.map(article => (
              <a key={article.slug} href={`/blog/${article.slug}`} className="block bg-[#12121a] border border-gray-800 rounded-xl p-6 hover:border-amber-500/30 transition-all duration-300">
                <div className="flex items-center gap-3 mb-2">
                  <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded font-medium">{article.category}</span>
                  <span className="text-gray-600 text-xs">{new Date(article.date).toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <h2 className="text-xl font-bold text-white mb-2 group-hover:text-amber-400">{article.title}</h2>
                <p className="text-gray-400 text-sm">{article.excerpt}</p>
                <span className="text-amber-400 text-sm font-medium mt-3 inline-block">Leggi di piu →</span>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Blog Article Page Component  
export const BlogArticlePage = () => {
  const slug = window.location.pathname.split('/blog/')[1];
  const article = blogArticles.find(a => a.slug === slug);

  const blogSchemaJson = useMemo(() => article ? JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "datePublished": article.date,
    "author": { "@type": "Organization", "name": "Edicola Cerasuolo" },
    "publisher": { "@type": "Organization", "name": "Edicola Cerasuolo", "url": "https://edicolavillaricca.com" }
  }) : "", [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <title>Articolo non trovato | Edicola Cerasuolo</title>
        <meta name="robots" content="noindex" />
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl text-white mb-4">Articolo non trovato</h1>
          <a href="/blog" className="text-amber-400">Torna al blog</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* React 19 native head tags */}
      <title>{`${article.title} | Edicola Cerasuolo`}</title>
      <meta name="description" content={article.excerpt} />
      <link rel="canonical" href={`https://edicolavillaricca.com/blog/${article.slug}`} />
      <meta property="og:title" content={`${article.title} | Edicola Cerasuolo`} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="article:published_time" content={article.date} />
      <meta property="article:section" content={article.category} />

      <Navigation />
      <nav className="bg-[#0d0d14] py-3 px-4 text-sm">
        <div className="max-w-7xl mx-auto">
          <ol className="flex items-center gap-2 text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <a href="/" className="hover:text-amber-400" itemProp="item"><span itemProp="name">Home</span></a><meta itemProp="position" content="1" />
            </li>
            <li className="text-gray-600">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <a href="/blog" className="hover:text-amber-400" itemProp="item"><span itemProp="name">Blog</span></a><meta itemProp="position" content="2" />
            </li>
            <li className="text-gray-600">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-amber-400" itemProp="name">{article.title}</span><meta itemProp="position" content="3" />
            </li>
          </ol>
        </div>
      </nav>
      <article className="py-12">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded font-medium">{article.category}</span>
            <time className="text-gray-600 text-xs" dateTime={article.date}>{new Date(article.date).toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' })}</time>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-6">{article.title}</h1>
          <div className="prose prose-invert max-w-none">
            {article.content.split('\n\n').map((paragraph, i) => (
              <p key={`${article.slug}-p${i}`} className="text-gray-300 text-base leading-relaxed mb-4">{paragraph}</p>
            ))}
          </div>
          <div className="mt-10 pt-6 border-t border-gray-800 flex gap-4">
            <a href="/blog" className="text-amber-400 hover:text-amber-300 text-sm font-medium">← Torna al blog</a>
            <a href="/prodotti" className="text-gray-500 hover:text-gray-400 text-sm">Vedi prodotti</a>
          </div>
        </div>
      </article>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: blogSchemaJson }} />
    </div>
  );
};




